import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Chart from 'react-apexcharts';
import { motion } from 'framer-motion';
import { FaToolbox, FaLightbulb, FaCalculator } from 'react-icons/fa';

const WorkCalculator = () => {
  const [force, setForce] = useState('');
  const [distance, setDistance] = useState('');
  const [angle, setAngle] = useState('');
  const [work, setWork] = useState('');
  const [showGraph, setShowGraph] = useState(false);

  const calculateWork = () => {
    if (force && distance) {
      const F = parseFloat(force);
      const d = parseFloat(distance);
      const theta = parseFloat(angle) || 0; // Default to 0 if not provided
      const W = F * d * Math.cos(theta * (Math.PI / 180));
      setWork(W.toFixed(2));
      setShowGraph(true);
    }
  };

  const generateGraphData = () => {
    if (!force || !distance) return null;

    const F = parseFloat(force);
    const d = parseFloat(distance);

    // Generate data for distance vs work
    const series = [
      {
        name: 'Work (J)',
        data: Array.from({ length: 10 }, (_, i) => {
          const currentDistance = d + (i - 5);
          return {
            x: currentDistance,
            y: F * currentDistance * Math.cos(parseFloat(angle) * (Math.PI / 180))
          };
        }),
      },
    ];

    const options = {
      chart: {
        type: 'line',
        height: 400,
        zoom: {
          enabled: false,
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: 'smooth',
        width: 3,
      },
      xaxis: {
        type: 'numeric',
        title: {
          text: 'Distance (m)',
        },
      },
      yaxis: {
        title: {
          text: 'Work (J)',
        },
      },
      title: {
        text: 'Work vs Distance',
        align: 'center',
      },
      markers: {
        size: 4,
        colors: ['#FF4560'],
      },
    };

    return { series, options };
  };

  return (
    <div className="min-h-screen bg-gradient-to-r from-blue-100 to-indigo-100">
      <Helmet>
        <title>Work Calculator | Physics Tool</title>
        <meta name="description" content="Calculate work done using the formula W = F × d × cos(θ). Interactive calculator with graph visualization for understanding the relationship between force, distance, and work." />
        <meta name="keywords" content="work calculator, physics calculator, energy calculator, physics mechanics calculator, force calculator" />
        <meta property="og:title" content="Work Calculator | Physics Tool" />
        <meta property="og:description" content="Calculate work done using the formula W = F × d × cos(θ). Interactive calculator with graph visualization for understanding the relationship between force, distance, and work." />
        <meta property="og:image" content="/social-preview.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Work Calculator | Physics Tool" />
        <meta name="twitter:description" content="Calculate work done using the formula W = F × d × cos(θ). Interactive calculator with graph visualization for understanding the relationship between force, distance, and work." />
        <link rel="canonical" href="https://yourdomain.com/physics-calculators/work-calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-blue-600 mb-4">
            <FaToolbox />
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Work Calculator</h1>
          <p className="text-xl text-gray-600">Calculate Work using W = F × d × cos(θ)</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-2xl font-bold mb-4">Calculator</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Force (N)</label>
                <input
                  type="number"
                  value={force}
                  onChange={(e) => setForce(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter force in Newtons"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Distance (m)</label>
                <input
                  type="number"
                  value={distance}
                  onChange={(e) => setDistance(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter distance in meters"
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-bold mb-2">Angle (°)</label>
                <input
                  type="number"
                  value={angle}
                  onChange={(e) => setAngle(e.target.value)}
                  className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                  placeholder="Enter angle between force and displacement (optional)"
                />
              </div>
              <div>
                <button
                  onClick={calculateWork}
                  className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
                >
                  Calculate Work
                </button>
              </div>

              {work && (
                <div className="mt-4 p-4 bg-blue-100 rounded">
                  <div className="flex items-center">
                    <FaCalculator className="text-2xl text-blue-600 mr-2" />
                    <div>
                      <h3 className="text-lg font-bold text-blue-800">Result</h3>
                      <div className="mt-2 space-y-2">
                        <p className="text-blue-800">Work = {work} J</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 p-4 bg-white rounded-lg shadow-md">
                    <div className="flex items-center mb-4">
                      <FaLightbulb className="text-yellow-500 mr-3" />
                      <h3 className="text-lg font-semibold">Solution Explanation</h3>
                    </div>
                    <p className="text-gray-600">
                      Using the work formula:
                      <br />
                      W = F × d × cos(θ)
                      <br />
                      <br />
                      Where:
                      <br />
                      F = {force} N
                      <br />
                      d = {distance} m
                      <br />
                      θ = {angle || 0}°
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {showGraph && (
            <div className="bg-white rounded-lg shadow-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Work vs Distance</h2>
              <Chart
                options={generateGraphData().options}
                series={generateGraphData().series}
                type="line"
                height={400}
              />
              <p className="mt-4 text-gray-600 text-center">
                Shows how work changes with distance
              </p>
            </div>
          )}
        </div>

        <div className="mt-12 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">How to Use</h2>
          <ol className="list-decimal list-inside space-y-2">
            <li>Enter the force applied in Newtons (N)</li>
            <li>Enter the distance moved in meters (m)</li>
            <li>Optionally enter the angle between force and displacement (default is 0°)</li>
            <li>Click "Calculate Work" to get the result</li>
            <li>View the interactive graph showing the relationship between work and distance</li>
          </ol>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">Features & Benefits</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-xl font-semibold mb-2">Accurate Calculations</h3>
              <p>Calculate work using the standard physics formula</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Interactive Graph</h3>
              <p>Visualize how work changes with distance</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Educational Tool</h3>
              <p>Ideal for physics students and educators</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">Step-by-Step Solutions</h3>
              <p>See how calculations are performed</p>
            </div>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold mb-4">FAQs</h2>
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What is Work in Physics?</h3>
              <p className="text-gray-600">
                Work is done when a force causes an object to move. It depends on the force, distance moved, and the angle between them.
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What units should I use?</h3>
              <p className="text-gray-600">
                For accurate calculations:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Force should be in Newtons (N)</li>
                  <li>Distance should be in meters (m)</li>
                  <li>Angle should be in degrees (°)</li>
                  <li>The result will be in Joules (J)</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">How does the graph work?</h3>
              <p className="text-gray-600">
                The graph shows how work changes with distance:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>X-axis represents distance (m)</li>
                  <li>Y-axis represents work (J)</li>
                  <li>The line shows the linear relationship between distance and work</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">What are some real-world applications?</h3>
              <p className="text-gray-600">
                Work calculations are important in many areas:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Mechanical engineering</li>
                  <li>Physics demonstrations</li>
                  <li>Energy calculations</li>
                  <li>Machine design and analysis</li>
                </ul>
              </p>
            </div>

            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-xl font-semibold mb-2">Why is this calculator useful?</h3>
              <p className="text-gray-600">
                This calculator helps:
                <ul className="list-disc list-inside ml-4 mt-2">
                  <li>Students understand work concepts</li>
                  <li>Engineers calculate energy transfer</li>
                  <li>Teachers demonstrate physics principles</li>
                  <li>Anyone needing to calculate work done</li>
                </ul>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkCalculator;
